import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import EspacioCombobox from "@/components/EspacioCombobox";
import ClienteCombobox from "@/components/ClienteCombobox";
import ConfianzaCampos from "@/components/ConfianzaCampos";
import ImporteMontaje from "@/components/ImporteMontaje";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Sparkles, Loader2, Mail, Trash2, Check, ChevronRight,
  Inbox, AlertCircle, FileText,
} from "lucide-react";
import { toast } from "sonner";
import EmailToPeticionDialog from "@/components/EmailToPeticionDialog";
import TiposLineaMulti from "@/components/TiposLineaMulti";
import { ProyectoThumb } from "@/components/ProyectoThumb";
import { ComponentesBorradorEditor, tiposDesdeComponentes } from "@/components/ComponentesBorradorEditor";
import { FusionLineasPanel, DuplicadosBanner } from "@/components/BandejaIAExtras";
import { aplicarFusion } from "@/lib/fusionLineas";

/** Bandeja IA — borradores de Petición generados desde emails parseados.
 *  Workflow: ver lista → abrir uno → editar campos → aprobar (crea Petición + Líneas).
 */
export default function BandejaIA() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [espacios, setEspacios] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState("pendiente_validar");
  const [sel, setSel] = useState(null);  // borrador seleccionado para validar

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, c, e] = await Promise.all([
        api.get(`/peticiones-ia?estado=${filtroEstado}`),
        api.get("/clientes"),
        api.get("/espacios"),
      ]);
      setItems(b.data); setClientes(c.data); setEspacios(e.data);
      try { const u = await api.get("/users"); setUsuarios((u.data || []).filter((x) => (x.roles || []).some((r) => ["admin", "comercial"].includes(r)))); } catch { /* sin permiso: selector vacío */ }
    } catch (err) {
      toast.error("Error cargando bandeja IA");
    } finally { setLoading(false); }
  }, [filtroEstado]);

  useEffect(() => { load(); }, [load]);

  const descartar = async (id) => {
    if (!window.confirm("¿Descartar este borrador? La acción no se puede deshacer.")) return;
    try {
      await api.delete(`/peticiones-ia/${id}`);
      toast.success("Borrador descartado");
      load();
    } catch { toast.error("Error descartando"); }
  };

  return (
    <div className="p-8 max-w-[1800px]" data-testid="bandeja-ia-page">
      <PageHeader
        title="Bandeja IA · Peticiones por validar"
        subtitle="Borradores generados a partir de emails reenviados por el equipo."
        action={
          <div className="flex gap-2">
            <Select value={filtroEstado} onValueChange={setFiltroEstado}>
              <SelectTrigger className="w-44 bg-zinc-900 border-zinc-800" data-testid="filter-estado">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                <SelectItem value="pendiente_validar">⏳ Pendientes</SelectItem>
                <SelectItem value="aprobado">✅ Aprobados</SelectItem>
                <SelectItem value="descartado">🗑 Descartados</SelectItem>
                <SelectItem value="todos">Todos</SelectItem>
              </SelectContent>
            </Select>
            <EmailToPeticionDialog onCreated={() => load()} />
          </div>
        }
      />

      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando…
        </div>
      ) : items.length === 0 ? (
        <div className="bg-zinc-900/30 border border-zinc-800/60 rounded-xl p-12 text-center">
          <Inbox className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
          <div className="text-zinc-300 font-medium">No hay borradores {filtroEstado === "pendiente_validar" ? "pendientes" : ""}</div>
          <div className="text-xs text-zinc-500 mt-2 max-w-md mx-auto">
            Cuando reenvíes un email del cliente a la app y le pegues el contenido en "Crear desde email", el borrador aparecerá aquí
            para que lo valides y se convierta en Petición real.
          </div>
        </div>
      ) : (
        <div className="space-y-2" data-testid="borradores-list">
          {items.map((b) => (
            <BorradorRow key={b.id} b={b}
              cliente={clientes.find((c) => c.id === b.cliente_existente_id)}
              onOpen={() => setSel(b)}
              onDelete={() => descartar(b.id)} />
          ))}
        </div>
      )}

      {sel && (
        <ValidarDialog
          borrador={sel}
          clientes={clientes}
          espacios={espacios}
          usuarios={usuarios}
          onClose={() => setSel(null)}
          onApproved={(peticionId) => {
            setSel(null);
            load();
            toast.success("Petición creada");
            setTimeout(() => nav(`/peticiones/${peticionId}`), 200);
          }}
        />
      )}
    </div>
  );
}

function ImagenSugeridaPicker({ borrador, value, onChange }) {
  const imgs = (borrador.attachments_meta || []).filter(
    (a) => (a.content_type || "").startsWith("image/") && a.url
  );
  if (imgs.length === 0 && !value) return null;
  const thumbOf = (url) => url.includes("/upload/")
    ? url.replace("/upload/", "/upload/c_fill,w_128,h_128,q_auto,f_auto/") : url;
  return (
    <div className="border border-zinc-800 bg-zinc-900/40 rounded-lg p-3" data-testid="imagen-sugerida-block">
      <div className="flex items-center justify-between mb-2">
        <Label className="text-xs text-zinc-400">
          📸 Imagen del proyecto{borrador.imagen_sugerida_url ? " · sugerida por IA" : ""}
        </Label>
        {value && (
          <button type="button" onClick={() => onChange("")}
            className="text-[10px] text-red-400 hover:underline" data-testid="imagen-sugerida-quitar">
            Quitar
          </button>
        )}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {imgs.map((a, i) => (
          <button key={i} type="button" onClick={() => onChange(a.url)} title={a.file_name}
            data-testid={`imagen-candidata-${i}`}
            className={`rounded-lg overflow-hidden border-2 transition-colors ${
              value === a.url ? "border-emerald-400" : "border-zinc-700 hover:border-violet-400"}`}>
            <img src={thumbOf(a.url)} alt={a.file_name || ""} className="w-16 h-16 object-cover" />
          </button>
        ))}
        {imgs.length === 0 && value && (
          <img src={thumbOf(value)} alt="" className="w-16 h-16 object-cover rounded-lg border-2 border-emerald-400" />
        )}
      </div>
      <div className="text-[10px] text-zinc-500 mt-2">
        {value
          ? "Se asignará como miniatura de la petición al aprobar."
          : "Ninguna seleccionada — la petición se creará sin imagen (podrás añadirla después)."}
      </div>
    </div>
  );
}

function ConfianzaBadge({ score }) {
  if (score === undefined || score === null) return null;
  const pct = Math.round(score * 100);
  const cls = pct >= 80 ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
    : pct >= 50 ? "bg-amber-500/15 text-amber-300 border-amber-500/30"
    : "bg-red-500/15 text-red-300 border-red-500/30";
  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] border font-mono ${cls}`}>
      {pct}%
    </span>
  );
}

function BorradorRow({ b, cliente, onOpen, onDelete }) {
  const isPendiente = b.estado === "pendiente_validar";
  const sinCliente = !b.cliente_existente_id;
  return (
    <div className={`group border rounded-lg p-4 transition-colors cursor-pointer ${
      isPendiente ? "bg-zinc-900/40 border-zinc-800 hover:border-violet-500/40 hover:bg-violet-500/5"
        : "bg-zinc-900/20 border-zinc-900 opacity-70 hover:opacity-100"
    }`} onClick={onOpen} data-testid={`borrador-row-${b.id}`}>
      <div className="flex items-start gap-3">
        <div className="shrink-0 mt-1 flex items-center gap-2">
          {b.estado === "pendiente_validar" ? <Mail className="w-5 h-5 text-violet-400" />
            : b.estado === "aprobado" ? <Check className="w-5 h-5 text-emerald-400" />
            : <FileText className="w-5 h-5 text-zinc-500" />}
          {b.imagen_sugerida_url && (
            <ProyectoThumb url={b.imagen_sugerida_url} nombre={b.cliente_nombre_sugerido || ""} size={40} rounded="lg"
              data-testid={`borrador-thumb-${b.id}`} />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <div className="text-sm font-semibold text-zinc-100 truncate">
              {b.email_subject || b.raw_resumen?.slice(0, 80) || "Sin asunto"}
            </div>
            {b.origen === "reenvio_automatico" ? (
              <span className="text-[10px] px-1.5 py-0.5 rounded border bg-emerald-500/10 text-emerald-300 border-emerald-500/30 inline-flex items-center gap-1">
                📨 Auto
              </span>
            ) : (
              <span className="text-[10px] px-1.5 py-0.5 rounded border bg-zinc-700/30 text-zinc-400 border-zinc-700">
                📋 Manual
              </span>
            )}
            {b.marca && (
              <span className="text-[10px] px-1.5 py-0.5 rounded border bg-violet-500/10 text-violet-300 border-violet-500/30">
                {b.marca}
              </span>
            )}
            {b.attachments_meta?.length > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded border bg-sky-500/10 text-sky-300 border-sky-500/30">
                📎 {b.attachments_meta.length}
              </span>
            )}
          </div>

          <div className="flex items-center gap-3 text-xs text-zinc-400 flex-wrap">
            <span className="font-medium text-zinc-300">
              {cliente?.nombre || b.cliente_nombre_sugerido || "—"}
            </span>
            {sinCliente && isPendiente && (
              <span className="inline-flex items-center gap-1 text-amber-300">
                <AlertCircle className="w-3 h-3" /> Cliente no encontrado
              </span>
            )}
            <ConfianzaBadge score={b.cliente_match_score} />
            {b.email_origen && <span className="font-mono text-[10px] text-zinc-500">{b.email_origen}</span>}
            {b.fecha_objetivo && (
              <span className="text-zinc-500">📅 {b.fecha_objetivo}</span>
            )}
            {b.lineas_sugeridas?.length > 0 && (
              <span className="text-emerald-400 text-[11px]">
                {b.lineas_sugeridas.length} línea(s) · {b.espacios_detectados?.length || 0} espacio(s)
              </span>
            )}
          </div>

          {b.raw_resumen && (
            <div className="text-xs text-zinc-500 mt-2 line-clamp-2">{b.raw_resumen}</div>
          )}

          <div className="text-[10px] text-zinc-600 mt-2">
            Reenviado por {b.forwarded_by_name || "—"} · {new Date(b.created_at).toLocaleString("es-ES")}
            {b.estado === "aprobado" && b.peticion_creada_id && (
              <span className="ml-2 text-emerald-400">→ Petición {b.peticion_creada_id.slice(0, 8)}</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          {isPendiente && (
            <>
              <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onDelete(); }}
                className="h-8 w-8 p-0 text-zinc-500 hover:text-red-300"
                data-testid={`btn-discard-${b.id}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
              <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-violet-400" />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ValidarDialog({ borrador, clientes, espacios, usuarios = [], onClose, onApproved }) {
  const [form, setForm] = useState({
    cliente_id: borrador.cliente_existente_id || "",
    nombre: borrador.asunto_normalizado
            || borrador.email_subject?.replace(/^(FW:|Fwd:|RE:|Re:)\s*/i, "").trim()
            || borrador.campana
            || borrador.marca
            || "Petición desde email",
    comercial_id: borrador.comercial_sugerido?.usuario_id || "",
    marca: borrador.marca || "",
    fecha_solicitud: borrador.fecha_solicitud || "",
    fecha_objetivo: borrador.fecha_objetivo || "",
    referencia_externa: borrador.referencia_externa || "",
    email_origen: borrador.email_origen || "",
    descripcion: borrador.descripcion || "",
    notas: borrador.raw_resumen || "",
    imagen_url: borrador.imagen_sugerida_url || "",
  });
  // Líneas: para cada una mapeamos espacio_idx → espacio_id real
  const [lineas, setLineas] = useState(() => {
    const espaciosDet = borrador.espacios_detectados || [];
    return (borrador.lineas_sugeridas || []).map((ln) => {
      const espDet = ln.espacio_idx != null ? espaciosDet[ln.espacio_idx] : null;
      return {
        tipos: Array.isArray(ln.tipos) && ln.tipos.length > 0
          ? ln.tipos
          : (ln.tipo ? [ln.tipo] : ["montaje"]),
        descripcion: ln.descripcion || "",
        espacio_id: espDet?.espacio_existente_id || "",
        _espacio_idx: ln.espacio_idx,  // mantenemos referencia al índice IA
        fecha_programada: ln.fecha_programada || "",
        referencia_cliente: ln.referencia_cliente || "",
        _espacio_sugerido_nombre: espDet?.nombre || "",
        _espacio_candidatos: espDet?.candidatos_top3 || [],  // Iter 60.11
        _espacio_motivo: espDet?.match_motivo || "",  // Sep 2026 · por qué se preseleccionó (asunto + fila)
        _espacio_preseleccionado: espDet?.espacio_existente_id || "",
        _avisos: Array.from(new Set([...(ln.avisos || []), ...((espDet?.avisos) || [])])),
        seccion: ln.seccion || "",
        firma: ln.firma || "",
        componentes: (ln.componentes || []).map((c) => ({ ...c, descripcion: c.descripcion || c.descripcion_sugerida || "" })),
      };
    });
  });
  const [saving, setSaving] = useState(false);
  // Iter 2 · A) duplicados por hash/message-id/asunto · B) fusión manual de líneas
  const [dups, setDups] = useState(null);
  const [continuarDup, setContinuarDup] = useState(false);
  const [selFusion, setSelFusion] = useState([]);
  const [fusionOpen, setFusionOpen] = useState(false);
  const navDup = useNavigate();
  useEffect(() => {
    let alive = true;
    api.get(`/peticiones-ia/${borrador.id}/duplicados`, { params: form.cliente_id ? { cliente_id: form.cliente_id } : {} })
      .then((r) => { if (alive) setDups(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, [borrador.id, form.cliente_id]);
  const hayDupFuerte = (dups?.exactos || []).length > 0;
  const toggleSelFusion = (i) => setSelFusion((s) => (s.includes(i) ? s.filter((x) => x !== i) : s.length >= 2 ? [s[1], i] : [...s, i]));
  const confirmarFusion = (elecciones) => {
    const [i, j] = selFusion;
    setLineas((ls) => aplicarFusion(ls, Math.min(i, j), Math.max(i, j), elecciones));
    setSelFusion([]); setFusionOpen(false);
    toast.success("Líneas fusionadas (revisa el resultado antes de aprobar)");
  };

  // Iter 84.3 (Fernando 17/jun) — En la bandeja IA SIEMPRE mostramos todos los
  // espacios. Antes filtrábamos por cliente_id === form.cliente_id, pero esto
  // rompe para clientes que actúan como instaladores (Decor Galva monta para
  // muchas marcas en muchos PDVs que pertenecen a OTROS clientes en BD).
  // El EspacioCombobox ya marca con badge "CLIENTE" los del cliente actual,
  // así que mantenemos la pista visual sin restringir la selección.
  const espaciosCliente = espacios;

  const aprobar = async () => {
    if (!form.cliente_id) {
      toast.error("Selecciona un cliente válido");
      return;
    }
    if (!form.nombre.trim()) {
      toast.error("El nombre de la petición no puede estar vacío");
      return;
    }
    if (hayDupFuerte && !continuarDup) {
      toast.error("Coincidencia fuerte con una petición ya procesada: abre la existente o marca «continuar creando nueva»");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        forzar_duplicado: !!continuarDup,
        imagen_url: form.imagen_url || null,
        fecha_solicitud: form.fecha_solicitud || null,
        fecha_objetivo: form.fecha_objetivo || null,
        lineas: lineas.map(({ _espacio_sugerido_nombre, _espacio_idx, _espacio_candidatos, _espacio_motivo, _espacio_preseleccionado, _avisos, ...rest }) => ({
          ...rest,
          tipo: (rest.tipos && rest.tipos[0]) || "montaje",  // legacy field
          espacio_id: rest.espacio_id || null,
          fecha_programada: rest.fecha_programada || null,
          referencia_cliente: rest.referencia_cliente || null,
          componentes: (rest.componentes || []).map((c) => ({
            ...c,
            ancho_cm: c.ancho_cm === "" || c.ancho_cm == null ? null : Number(String(c.ancho_cm).replace(",", ".")),
            alto_cm: c.alto_cm === "" || c.alto_cm == null ? null : Number(String(c.alto_cm).replace(",", ".")),
            nombre: c.descripcion || c.descripcion_sugerida || c.material || "Componente",
          })),
        })),
      };
      const { data } = await api.post(`/peticiones-ia/${borrador.id}/aprobar`, payload);
      onApproved(data.peticion_id);
    } catch (err) {
      const det = err?.response?.data?.detail;
      if (det?.code === "posible_duplicado") { setDups({ exactos: det.exactos, posibles: det.posibles }); toast.error(det.mensaje); }
      else toast.error(typeof det === "string" ? det : "Error aprobando borrador");
    } finally { setSaving(false); }
  };

  const updateLinea = (i, patch) => setLineas((ls) => ls.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const removeLinea = (i) => setLineas((ls) => ls.filter((_, idx) => idx !== i));
  const addLinea = () => setLineas((ls) => [...ls, { tipos: ["montaje"], descripcion: "", espacio_id: "", fecha_programada: "", referencia_cliente: "", componentes: [], _avisos: [] }]);
  const duplicarLinea = (i) => setLineas((ls) => [...ls.slice(0, i + 1), { ...ls[i], componentes: (ls[i].componentes || []).map((c) => ({ ...c })) }, ...ls.slice(i + 1)]);
  const setComponentes = (i, comps) => updateLinea(i, { componentes: comps, tipos: comps.length ? tiposDesdeComponentes(comps) : lineas[i].tipos });

  const crearEspacio = async (lineaIdx, espacioIdx) => {
    if (!form.cliente_id) {
      toast.error("Selecciona cliente antes de crear el espacio");
      return;
    }
    try {
      const { data } = await api.post(`/peticiones-ia/${borrador.id}/crear-espacio`, {
        idx: espacioIdx,
        cliente_id: form.cliente_id,
      });
      // Actualiza la línea con el espacio_id nuevo
      setLineas((ls) => ls.map((l, i) => i === lineaIdx
        ? { ...l, espacio_id: data.espacio.id }
        : l));
      toast.success(`✅ Espacio "${data.espacio.nombre}" creado`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error creando espacio");
    }
  };

  const conf = borrador.confianzas || {};

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-violet-400" />
            Validar borrador IA
          </DialogTitle>
          <DialogDescription className="sr-only">
            Revisa y edita los datos extraídos por la IA antes de crear la petición.
          </DialogDescription>
        </DialogHeader>
        <ConfianzaCampos confianzas={borrador.confianzas_campos} />
        {borrador.modo === "alta_en_peticion" && (
          <div className="text-xs rounded border border-sky-500/40 bg-sky-500/10 p-2" data-testid="borrador-alta-en-peticion">
            <b>Añadido sobre petición existente {borrador.peticion_destino_codigo}</b> · evento «{borrador.tipo_evento}». Al aprobar SOLO se añaden las líneas de abajo; el resto de la petición no se toca.
            {(borrador.motivos_evento || []).length > 0 && <div className="text-zinc-400 mt-1">{borrador.motivos_evento.join(" · ")}</div>}
          </div>
        )}

        {/* Resumen IA */}
        {borrador.raw_resumen && (
          <div className="bg-violet-500/5 border border-violet-500/20 rounded-lg p-3 text-xs text-violet-200">
            🤖 {borrador.raw_resumen}
          </div>
        )}
        {(borrador.avisos || []).length > 0 && (
          <div className="bg-amber-500/10 border border-amber-500/40 rounded-lg p-3 text-xs text-amber-100 space-y-0.5" data-testid="borrador-avisos">
            <div className="font-semibold text-amber-200">⚠️ Revisar antes de aprobar</div>
            {borrador.avisos.map((a, i) => <div key={i}>• {a}</div>)}
          </div>
        )}
        <div className="text-[10px] text-zinc-500 -mt-1">La IA sólo propone: nada se crea hasta que pulses «Aprobar». Ningún espacio se crea automáticamente.</div>
        <DuplicadosBanner dups={dups} continuar={continuarDup} onContinuar={setContinuarDup}
          onAbrir={(pid) => { onClose(); navDup(`/peticiones/${pid}`); }} onCancelar={onClose} />

        {/* Imagen del proyecto sugerida por IA */}
        <ImagenSugeridaPicker borrador={borrador} value={form.imagen_url}
          onChange={(u) => setForm({ ...form, imagen_url: u })} />

        {/* Campos principales */}
        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-12">
            <Label className="text-xs text-zinc-400">Nombre de la petición *</Label>
            <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              className="bg-zinc-900 border-zinc-800 mt-1" data-testid="validar-nombre" />
          </div>
          <div className="col-span-8">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-zinc-400">Cliente *</Label>
              {borrador.email_meta?.cliente_id_seleccionado ? (
                <span className={`text-[10px] px-1.5 py-0.5 rounded border ${borrador.email_meta.perfil === "decor_galva" ? "bg-amber-500/15 border-amber-500/40 text-amber-200" : "bg-sky-500/10 border-sky-500/30 text-sky-200"}`}
                  data-testid="borrador-perfil-badge">
                  {borrador.email_meta.perfil === "decor_galva" ? "Perfil Decor Galvá · cliente elegido a mano" : "Perfil genérico · cliente elegido a mano"}
                  {borrador.email_meta.tablas_detectadas > 0 && ` · ${borrador.email_meta.filas_tablas} filas de tabla`}
                </span>
              ) : <ConfianzaBadge score={conf.cliente} />}
              {borrador.contexto_asunto?.ensenas?.length > 0 && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded border ${borrador.contexto_asunto.ambigua ? "bg-amber-500/10 border-amber-500/40 text-amber-200" : "bg-emerald-500/10 border-emerald-500/30 text-emerald-200"}`}
                  data-testid="borrador-ensena-asunto">
                  {borrador.contexto_asunto.ambigua ? `Asunto: varias enseñas (${borrador.contexto_asunto.ensenas.join(", ")})` : `Enseña del asunto: ${borrador.contexto_asunto.ensena}`}
                </span>
              )}
            </div>
            <ClienteCombobox
              value={form.cliente_id}
              onChange={(id) => setForm((f) => ({ ...f, cliente_id: id }))}
              clientes={clientes}
              placeholder="— Selecciona cliente —"
              testId="validar-cliente"
              className="mt-1"
            />
            {!form.cliente_id && borrador.cliente_nombre_sugerido && (
              <div className="text-[10px] text-amber-300 mt-1">
                IA sugirió: "{borrador.cliente_nombre_sugerido}" — créalo manualmente si no existe.
              </div>
            )}
          </div>
          <div className="col-span-4">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-zinc-400">Marca</Label>
              <ConfianzaBadge score={conf.marca} />
            </div>
            <Input value={form.marca} onChange={(e) => setForm({ ...form, marca: e.target.value })}
              className="bg-zinc-900 border-zinc-800 mt-1" />
          </div>

          <div className="col-span-12">
            <Label className="text-xs text-zinc-400">Comercial {borrador.comercial_sugerido?.motivo && <span className="text-zinc-600">· sugerido por {borrador.comercial_sugerido.motivo}</span>}</Label>
            <select value={form.comercial_id} onChange={(e) => setForm({ ...form, comercial_id: e.target.value })}
              data-testid="validar-comercial"
              className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded-md h-9 text-sm px-2 text-zinc-200">
              <option value="">— Yo mismo (quien aprueba) —</option>
              {usuarios.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>

          <div className="col-span-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-zinc-400">Fecha solicitud</Label>
              <ConfianzaBadge score={conf.fechas} />
            </div>
            <Input type="date" value={form.fecha_solicitud}
              onChange={(e) => setForm({ ...form, fecha_solicitud: e.target.value })}
              className="bg-zinc-900 border-zinc-800 mt-1" />
          </div>
          <div className="col-span-3">
            <Label className="text-xs text-zinc-400">Fecha objetivo</Label>
            <Input type="date" value={form.fecha_objetivo}
              onChange={(e) => setForm({ ...form, fecha_objetivo: e.target.value })}
              className="bg-zinc-900 border-zinc-800 mt-1" />
          </div>
          <div className="col-span-3">
            <Label className="text-xs text-zinc-400">Referencia ext.</Label>
            <Input value={form.referencia_externa}
              onChange={(e) => setForm({ ...form, referencia_externa: e.target.value })}
              className="bg-zinc-900 border-zinc-800 mt-1" />
          </div>
          <div className="col-span-3">
            <Label className="text-xs text-zinc-400">Email origen</Label>
            <Input value={form.email_origen}
              onChange={(e) => setForm({ ...form, email_origen: e.target.value })}
              className="bg-zinc-900 border-zinc-800 mt-1 font-mono text-xs" />
          </div>

          <div className="col-span-12">
            <Label className="text-xs text-zinc-400">Descripción</Label>
            <Textarea value={form.descripcion}
              onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
              className="bg-zinc-900 border-zinc-800 mt-1" rows={3} />
          </div>
        </div>

        {/* Líneas */}
        <div className="mt-4 border-t border-zinc-800 pt-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Label className="text-sm text-zinc-200 font-semibold">Líneas a crear</Label>
              <ConfianzaBadge score={conf.lineas} />
            </div>
            <div className="flex items-center gap-2">
              {lineas.length >= 2 && (
                <Button size="sm" variant="outline" disabled={selFusion.length !== 2} onClick={() => setFusionOpen(true)}
                  data-testid="fusionar-lineas-btn" title="Marca 2 líneas con la casilla «fusionar» para unirlas manualmente"
                  className="bg-transparent border-amber-500/40 text-amber-200 hover:bg-amber-500/10 h-7 text-xs disabled:opacity-40">
                  ⇄ Fusionar líneas {selFusion.length ? `(${selFusion.length}/2)` : ""}
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={addLinea}
                className="bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-800 h-7 text-xs">
                + Añadir línea
              </Button>
            </div>
          </div>
          {fusionOpen && selFusion.length === 2 && (
            <div className="mb-2">
              <FusionLineasPanel a={lineas[Math.min(...selFusion)]} b={lineas[Math.max(...selFusion)]}
                idxA={Math.min(...selFusion)} idxB={Math.max(...selFusion)} espacios={espaciosCliente}
                onConfirm={confirmarFusion} onCancel={() => setFusionOpen(false)} />
            </div>
          )}
          {lineas.length === 0 ? (
            <div className="text-xs text-zinc-500 italic py-3">Sin líneas detectadas. Puedes añadirlas manualmente.</div>
          ) : (
            <div className="space-y-2">
              {lineas.map((ln, i) => (
                <div key={i} className={`border rounded-lg p-3 ${selFusion.includes(i) ? "border-amber-500/60 bg-amber-500/5" : "border-zinc-800 bg-zinc-900/40"}`}
                  data-testid={`linea-borrador-${i}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] text-zinc-500 font-mono">Línea #{i + 1}{ln._fusionada && <span className="ml-1 text-amber-300">· fusionada</span>}</span>
                    {lineas.length >= 2 && (
                      <label className="flex items-center gap-1 text-[10px] text-zinc-400 cursor-pointer">
                        <input type="checkbox" checked={selFusion.includes(i)} onChange={() => toggleSelFusion(i)}
                          data-testid={`fusion-sel-${i}`} className="accent-amber-500" /> fusionar
                      </label>
                    )}
                  </div>
                  <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-3">
                    <Label className="text-[10px] text-zinc-500">Espacio</Label>
                    <EspacioCombobox
                      value={ln.espacio_id}
                      onChange={(v) => updateLinea(i, { espacio_id: v || "" })}
                      espacios={espaciosCliente}
                      clienteId={form.cliente_id}
                      placeholder="Buscar espacio…"
                      allowNone
                      testId={`bandeja-espacio-${i}`}
                      className="h-8"
                    />
                    {ln.espacio_id && ln._espacio_motivo && ln.espacio_id === ln._espacio_preseleccionado && (
                      <div className="text-[9px] text-emerald-300/90 mt-1 leading-tight" data-testid={`match-motivo-${i}`}>✓ {ln._espacio_motivo}</div>
                    )}
                    {ln._espacio_sugerido_nombre && !ln.espacio_id && (
                      <div className="text-[9px] text-amber-300 mt-1 flex items-center justify-between gap-1">
                        <span className="truncate">IA: "{ln._espacio_sugerido_nombre}"</span>
                        {form.cliente_id && ln._espacio_idx != null && (
                          <button
                            type="button"
                            onClick={() => crearEspacio(i, ln._espacio_idx)}
                            data-testid={`btn-crear-espacio-${i}`}
                            className="text-emerald-300 hover:text-emerald-200 underline text-[9px] whitespace-nowrap">
                            ➕ Crear
                          </button>
                        )}
                      </div>
                    )}
                    {/* Iter 60.11 — Sugerir top 3 espacios similares cuando no hay match >=0.6 */}
                    {!ln.espacio_id && ln._espacio_candidatos && ln._espacio_candidatos.length > 0 && (
                      <div className="mt-1.5 space-y-0.5" data-testid={`candidatos-espacio-${i}`}>
                        <div className="text-[9px] text-zinc-500">¿Quizás alguno de estos?</div>
                        {ln._espacio_candidatos.map((c) => (
                          <button key={c.id} type="button"
                            onClick={() => updateLinea(i, { espacio_id: c.id })}
                            data-testid={`candidato-esp-${i}-${c.id}`}
                            className="w-full text-left px-1.5 py-1 rounded bg-zinc-800/60 hover:bg-violet-500/20 border border-zinc-700 hover:border-violet-500/40 text-[10px] text-zinc-300 hover:text-violet-200">
                            <div className="flex items-center justify-between gap-1">
                              <span className="truncate">{c.nombre} <span className="text-zinc-500">· {c.ciudad}</span></span>
                              <span className="text-violet-300 text-[9px] shrink-0">{Math.round(c.score * 100)}%</span>
                            </div>
                            {c.motivo && <div className="text-[9px] text-zinc-500 truncate" data-testid={`candidato-motivo-${i}-${c.id}`}>{c.motivo}</div>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="col-span-5">
                    <Label className="text-[10px] text-zinc-500">Descripción</Label>
                    <Input value={ln.descripcion} onChange={(e) => updateLinea(i, { descripcion: e.target.value })}
                      className="bg-zinc-950 border-zinc-800 h-8 text-xs" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-[10px] text-zinc-500">Fecha</Label>
                    <Input type="date" value={ln.fecha_programada || ""}
                      onChange={(e) => updateLinea(i, { fecha_programada: e.target.value })}
                      className="bg-zinc-950 border-zinc-800 h-8 text-[10px] px-1" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-[10px] text-zinc-500">Ref. cliente</Label>
                    <Input value={ln.referencia_cliente || ""}
                      onChange={(e) => updateLinea(i, { referencia_cliente: e.target.value })}
                      className="bg-zinc-950 border-zinc-800 h-8 text-[10px] px-1" />
                  </div>
                  </div>
                  <ImporteMontaje linea={ln} idx={i} clienteId={form.cliente_id} onChange={(patch) => updateLinea(i, patch)}
                    onAplicarTodos={(v) => setLineas((ls) => ls.map((l) => (l.tipos || []).includes("montaje") ? { ...l, precio_montaje: v, precio_montaje_origen: l.precio_montaje_origen || "manual" } : l))} />
                  <div className="mt-2 pl-1">
                    <Label className="text-[10px] text-zinc-500 mb-1 block">Tipos (puedes combinar varios)</Label>
                    <TiposLineaMulti value={ln.tipos || []}
                      onChange={(v) => updateLinea(i, { tipos: v })}
                      compact
                      dataTestId={`tipos-linea-${i}`} />
                  </div>
                  {(ln._avisos || []).length > 0 && (
                    <div className="mt-1.5 text-[10px] text-amber-300 space-y-0.5" data-testid={`linea-avisos-${i}`}>
                      {ln._avisos.map((a, k) => <div key={k}>⚠ {a}</div>)}
                    </div>
                  )}
                  {(ln.seccion || ln.firma) && (
                    <div className="mt-1 text-[9px] text-zinc-500">{ln.seccion && <>Sección: {ln.seccion}</>}{ln.seccion && ln.firma && " · "}{ln.firma && <>Firma: {ln.firma}</>}</div>
                  )}
                  <ComponentesBorradorEditor comps={ln.componentes || []} onChange={(cs) => setComponentes(i, cs)} lineaIdx={i} />
                  <div className="flex justify-end gap-2 -mt-1">
                    <Button size="sm" variant="ghost" onClick={() => duplicarLinea(i)} data-testid={`duplicar-linea-${i}`}
                      className="h-6 text-[10px] text-zinc-500 hover:text-amber-300">
                      Duplicar línea (mismo espacio)
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => removeLinea(i)}
                      className="h-6 text-[10px] text-zinc-500 hover:text-red-300">
                      Quitar línea
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Email crudo (colapsable) */}
        <details className="mt-3 text-xs">
          <summary className="cursor-pointer text-zinc-500 hover:text-zinc-300">📩 Ver email original</summary>
          <pre className="bg-zinc-950 border border-zinc-800 rounded p-3 mt-2 whitespace-pre-wrap text-[11px] text-zinc-400 max-h-64 overflow-y-auto">
            {borrador.email_raw}
          </pre>
        </details>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}
            className="bg-transparent border-zinc-700" disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={aprobar} disabled={saving || !form.cliente_id || !form.nombre.trim()}
            className="bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold"
            data-testid="btn-aprobar-borrador">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creando…</>
              : <><Check className="w-4 h-4 mr-2" /> {borrador.modo === "alta_en_peticion" ? "Aprobar y añadir líneas" : "Aprobar y crear Petición"}</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
